import { camelCase, isArray, isObject, transform } from 'lodash';
import moment from 'moment-timezone';

import { clampRangeToSpan } from '@src/components/RelativeAbsolutePeriodFilter';
import { clampCalendarRangeToToday, DateUtil } from '../utils/dates';

export const getDateTypeIndex = (seconds, groupBy, intl) => {
  switch (groupBy) {
    case 'hour':
      return moment(seconds).format(
        `${DateUtil.DEFAULT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`,
      );
    case 'month':
      return moment(seconds).format('MMMM - YYYY');
    case 'quarter':
      return intl.formatMessage(
        {
          id: 'dashboards.dateIndex.quarter',
        },
        { date: moment(seconds).format('Q - YYYY') },
      );
    case 'year':
      return moment(seconds).format('YYYY');
    case 'week':
      return intl.formatMessage(
        {
          id: 'dashboards.dateIndex.week',
        },
        { date: moment(seconds).format(`${DateUtil.DEFAULT_DATE_FORMAT}`) },
      );
    case 'day':
    default:
      return moment(seconds).format(DateUtil.DEFAULT_DATE_FORMAT);
  }
};

export const getDateTypeIndexUTC = (seconds, groupBy, intl) => {
  switch (groupBy) {
    case 'hour':
      return moment
        .utc(seconds)
        .format(`${DateUtil.DEFAULT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`);
    case 'month':
      return moment.utc(seconds).format('MMMM - YYYY');
    case 'quarter':
      return intl.formatMessage(
        {
          id: 'dashboards.dateIndex.quarter',
        },
        { date: moment.utc(seconds).format('Q - YYYY') },
      );
    case 'year':
      return moment.utc(seconds).format('YYYY');
    case 'week':
      return intl.formatMessage(
        {
          id: 'dashboards.dateIndex.week',
        },
        { date: moment.utc(seconds).format(DateUtil.DEFAULT_DATE_FORMAT) },
      );
    case 'day':
    default:
      return moment.utc(seconds).format(DateUtil.DEFAULT_DATE_FORMAT);
  }
};

/**
 * Month - belong to month of the record
 * Quarter - belong to quarter of the record
 * Year - belong to year of the record
 * Week - belong to week of the record
 * Day - belong to day of the record
 */

/**
 * Checks if a date falls within a specific time period based on a reference date.
 * @param {string} dateType - The type of period to check ('year', 'quarter', 'month', 'week', or 'day').
 * @param {string|Date} date - The reference date to compare against.
 * @param {string|Date} dateToMatch - The date to check.
 * @returns {boolean} True if dateToMatch is within the specified period of date.
 * @throws {Error} If an unsupported dateType is provided.
 */
export const isDateInPeriod = (dateType, dateKey, dateToMatch) => {
  const momentDateKey = moment.utc(dateKey);
  const momentToMatch = moment.utc(dateToMatch);
  const type = dateType.toLowerCase();

  switch (type) {
    case 'year':
      return momentDateKey.year() === momentToMatch.year();
    case 'quarter':
      return (
        momentDateKey.year() === momentToMatch.year() &&
        momentDateKey.quarter() === momentToMatch.quarter()
      );
    case 'month':
      return (
        momentDateKey.year() === momentToMatch.year() &&
        momentDateKey.month() === momentToMatch.month()
      );
    case 'week':
      return (
        momentDateKey.year() === momentToMatch.year() &&
        momentDateKey.week() === momentToMatch.week()
      );
    case 'day':
      return momentDateKey.isSame(momentToMatch, 'day');
    default:
      throw new Error(
        `Unsupported dateType: ${dateType}. Use 'year', 'quarter', 'month', 'week', or 'day'.`,
      );
  }
};

const CALENDAR_ALIGNED_UNITS = ['day', 'week', 'month', 'quarter', 'year'];

const isCalendarAlignedUnit = dateType =>
  CALENDAR_ALIGNED_UNITS.includes(moment.normalizeUnits(dateType));

/**
 * Builds the epoch-seconds range sent to the dashboard aggregations.
 *
 * Relative windows on calendar units mean "N units before, until today", so
 * they are day-aligned and reach the end of today: records scheduled later in
 * the current day (a schedule created minutes ago, still to start) stay inside
 * the range. Sub-day units (6h, 12h, 24h) keep their rolling behaviour.
 *
 * Legacy callers use UTC; calendar reports explicitly supply the organization time zone.
 */
export const getRange = (dateRange, dateType, isRelative, relativeAmount, maxSpan, timeZone, historicalOnly = false) => {
  if (isRelative) {
    const now = timeZone ? moment.tz(timeZone) : moment.utc();
    const relativeStart = now.clone().subtract(relativeAmount, dateType);

    if (!isCalendarAlignedUnit(dateType)) {
      return {
        gte: relativeStart.format('X'),
        lte: now.format('X'),
      };
    }

    const relativeRange = [relativeStart.startOf('day'), now.clone().endOf('day')];
    const [start, end] = maxSpan
      ? clampRangeToSpan(relativeRange, 'day', maxSpan)
      : relativeRange;

    return { gte: start.format('X'), lte: end.format('X') };
  }

  const [gte, lte] = dateRange;

  const start = timeZone ? moment.tz(gte.format('YYYY-MM-DD'), 'YYYY-MM-DD', timeZone) : moment.utc(gte.toDate());
  const end = timeZone ? moment.tz(lte.format('YYYY-MM-DD'), 'YYYY-MM-DD', timeZone) : moment.utc(lte.toDate());

  const expandedRange = [start.clone().startOf(dateType), end.clone().endOf(dateType)];
  const absoluteRange = historicalOnly
    ? clampCalendarRangeToToday(expandedRange, timeZone ?? 'UTC')
    : expandedRange;
  const [rangeStart, rangeEnd] = maxSpan
    ? clampRangeToSpan(absoluteRange, dateType, maxSpan)
    : absoluteRange;

  return { gte: rangeStart.format('X'), lte: rangeEnd.format('X') };
};

export const camelize = obj =>
  transform(obj, (acc, value, key, target) => {
    const camelKey = isArray(target) ? key : camelCase(key);

    acc[camelKey] = isObject(value) ? camelize(value) : value;
  });
